import { Button, buttonClass, cardClass, fieldClass, selectClass } from '@ledgerloom/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { propose } from '../api';
import { Field } from '../components/Field';
import { useApi, usePageTitle } from '../hooks';
import { useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Loading, outcomeMessage, outcomeTone, schemaLabel, StateChip, Time, useToast, when, ToneBadge } from '../components/ui';
import { Face } from '../components/TeamFace';
import { label, MemberCard, ROLE_NAME, sentence, type Member } from './Team';

// One person on the team, as an agent has its page: what they are doing now,
// their week, the agents that draft for them, and their latest decisions.
// Patterns: data-display/statistics for the week, data-display/card for their
// agents, data-display/list for decisions (each opens its proposal).

interface Person extends Member {
  role: string; role_name: string | null; teams: Array<{ id: string; name: string }>;
  week: { decided: number; approved: number; proposed: number };
  their_agents: Member[];
  decisions: Array<{ proposal_id: string; schema_id: string; state: string; agent_id: string | null; agent_name: string | null; decided_at: string }>;
}

interface Line { id: string; topic: string; claim: string; inferred: boolean; by: string; at: string; expires_at: string | null }

const TOPICS: Array<[string, string, string]> = [
  ['goal', 'Goals', 'Close the Maple St listing before December'],
  ['focus', 'Current focus', 'Getting the spring listings photographed'],
  ['responsibility', 'Responsibilities', 'Every buyer lead in the north side'],
  ['working_pattern', 'How {who} work', 'Calls in the morning, paperwork after three'],
  ['preference', 'Preferences', 'Short summaries, no greetings'],
];

// ADR 0036: the person's mental model, what their agents read about them. Theirs to add to
// and remove from; a manager reads it. Patterns: data-display/list-view, forms/selection-input.
function AboutPerson({ userId, mine, first = '' }: { userId: string; mine: boolean; first?: string }) {
  const r = useApi<{ about: Line[] }>(`/v1/person?user_id=${encodeURIComponent(userId)}`, [userId]);
  const toast = useToast();
  const [topic, setTopic] = useState('goal');
  const [claim, setClaim] = useState('');
  const [busy, setBusy] = useState(false);
  // Not one the caller may read: the section is not theirs to see.
  if (r.error || !r.data) return null;
  const lines = r.data.about;
  const send = async (schema: string, payload: Record<string, unknown>, done: string) => {
    setBusy(true);
    try {
      const o = await propose(schema, payload);
      toast(outcomeMessage(o, done), outcomeTone(o));
      if (o.state === 'written') { setClaim(''); await r.reload(); }
    } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  const who = mine ? 'you' : first;
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="about-h">
      <div>
        <h2 id="about-h">About {who}</h2>
        <p className="muted small" style={{ margin: 0 }}>
          What {mine ? 'your' : `${first}'s`} agents know about {mine ? 'you' : 'them'} when they plan and advise. {mine ? 'You' : first} and {mine ? 'your' : 'their'} managers see it.
          Lines marked noted from {mine ? 'your' : 'their'} work were added by an agent{mine ? '; remove any that are wrong' : ''}.
        </p>
      </div>
      {!lines.length && <p className="muted" style={{ margin: 0 }}>Nothing yet.{mine ? ' Add a goal or what you are focused on, and your agents plan around it.' : ''}</p>}
      {TOPICS.map(([key, title]) => {
        const these = lines.filter((l) => l.topic === key);
        if (!these.length) return null;
        return (
          <div key={key} className="stack" style={{ gap: 4 }}>
            <h3 className="small">{title.replace('{who}', who)}</h3>
            <ul className="about-lines" aria-label={title.replace('{who}', who)}>
              {these.map((l) => (
                <li key={l.id}>
                  <span>{l.claim}</span>
                  <span className="faint small">{l.inferred ? ` · noted from ${mine ? 'your' : 'their'} work by ${l.by}` : l.by === 'you' ? '' : ` · from ${l.by}`}{l.expires_at ? ` · until ${new Date(l.expires_at).toLocaleDateString()}` : ''}</span>
                  {mine && <Button type="button" variant="ghost" size="sm" disabled={busy} aria-label={`Remove: ${l.claim}`} onClick={() => void send('person_memory.retire', { memory_id: l.id }, 'Removed')}>Remove</Button>}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {mine && (
        <form className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }} onSubmit={(e) => { e.preventDefault(); if (claim.trim().length >= 3) void send('person_memory.capture', { topic, claim: claim.trim() }, 'Added'); }}>
          <Field label="Kind">
            <select className={selectClass} value={topic} onChange={(e) => setTopic(e.target.value)}>
              {TOPICS.map(([key, title]) => <option key={key} value={key}>{title.replace('{who}', 'you')}</option>)}
            </select>
          </Field>
          <Field label="What to add" className="grow">
            <input className={fieldClass} value={claim} maxLength={300} onChange={(e) => setClaim(e.target.value)} placeholder={TOPICS.find(([k]) => k === topic)?.[2]} />
          </Field>
          <Button type="submit" variant="secondary" disabled={busy || claim.trim().length < 3}>Add</Button>
        </form>
      )}
    </section>
  );
}

export function PersonDetail() {
  const { id = '' } = useParams();
  const { me } = useSession();
  const person = useApi<Person>(`/v1/team/people/${encodeURIComponent(id)}`, [id]);
  const p = person.data;
  usePageTitle(p?.name ?? null);

  if (person.loading && !p) return <Loading label="Loading the person" />;
  if (!p) return <div className="stack"><ErrorBanner error={person.error} /><Link to="/desk">Back to the Agent Desk</Link></div>;
  const mine = p.id === me.user_id;
  const first = p.name.split(/\s+/)[0];

  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/desk', label: 'Agent Desk' }, { label: p.name }]} />
      <div className="page-head">
        <div className="member-top">
          <Face kind="person" name={p.name} tone={p.tone} />
          <div>
            <div className="row"><h1>{p.name}</h1><ToneBadge tone={p.tone}>{label(p)}</ToneBadge></div>
            <p>{sentence(p)}</p>
            <p className="faint">
              {p.role_name ?? ROLE_NAME[p.role] ?? p.role}{p.teams.length ? ` · ${p.teams.map((t) => t.name).join(', ')}` : ''} · {p.last_seen_at ? `last seen ${when(p.last_seen_at)}` : 'not signed in yet'}
            </p>
          </div>
        </div>
        {mine && p.waiting > 0 && <Link className={buttonClass()} to="/desk/needs-you">See what needs you</Link>}
      </div>

      <section aria-label="This week" className="stats">
        <div className={`${cardClass} stat`}><div className="num">{p.waiting}</div><div className="label">Waiting on {mine ? 'you' : first}</div></div>
        <div className={`${cardClass} stat`}><div className="num">{p.week.decided}</div><div className="label">Decided this week</div></div>
        <div className={`${cardClass} stat`}><div className="num">{p.week.approved}</div><div className="label">Approved as proposed</div></div>
        <div className={`${cardClass} stat`}><div className="num">{p.week.proposed}</div><div className="label">Proposed this week</div></div>
      </section>

      <AboutPerson userId={p.id} mine={mine} first={first} />

      <section className="stack" style={{ gap: 12 }} aria-labelledby="agents-h">
        <h2 id="agents-h">Agents working for {mine ? 'you' : first}</h2>
        <p className="muted small" style={{ margin: 0 }}>Each sees what {mine ? 'you see' : `${first} sees`}, drafts in {mine ? 'your' : 'their'} name, and waits for {mine ? 'you' : first} to approve, edit, or reject each draft{mine ? ' under Needs you' : ''}.</p>
        {!p.their_agents.length ? <div className={cardClass}><Empty title="No agents yet">An admin or manager can hire one to work for {mine ? 'you' : first}.</Empty></div> : (
          <ul className="member-grid">{p.their_agents.map((m) => <li key={m.id}><MemberCard m={m} /></li>)}</ul>
        )}
      </section>

      <section className={cardClass} aria-labelledby="decisions-h">
        <div className="card-head"><div><h2 id="decisions-h">Recent decisions</h2><div className="faint">Open one for what was proposed and the whole trail.</div></div></div>
        {!p.decisions.length ? <Empty title="No decisions yet" /> : (
          <ul className="run-list">
            {p.decisions.map((d) => (
              <li key={d.proposal_id}>
                <Link className="run-row" to={`/activity/${d.proposal_id}`}>
                  <div className="run-head">
                    <span className="item-title">{schemaLabel(d.schema_id)}</span>
                    <StateChip state={d.state} />
                    <span className="faint small run-meta">{d.agent_name ? `from ${d.agent_name}` : 'from a person'} · <Time iso={d.decided_at} /></span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
